import { smartTruncate } from '@infinex-private/connect-web-shared/utils';
import type { BlockaidValidationResult } from '@infinex/connect-service-contract/client';

import {
  getContractAddress,
  getContractName,
  getFunctionName,
  formatFunctionName,
} from './evmHelpers';
import {
  getSolanaActions,
  getSolanaPrograms,
  type ProgramDetails,
} from './solanaHelpers';

// Re-export commonly used functions
export { formatFunctionName, smartTruncate };
export type { ProgramDetails };

/**
 * Format contract address for display
 */
export function formatContractAddress(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// EVM-specific transaction details
export interface EVMTransactionDetails {
  type: 'evm';
  contractAddress: string;
  contractName: string;
  functionName: string;
}

// Solana-specific transaction details
export interface SolanaTransactionDetails {
  type: 'solana';
  programs: ProgramDetails[];
  actions: string;
}

// Union type for all transaction details
export type TransactionDetails =
  | EVMTransactionDetails
  | SolanaTransactionDetails;

/**
 * Extract all transaction details at once
 */
export function extractTransactionDetails(
  txnValidationResult: BlockaidValidationResult | null
): TransactionDetails | null {
  if (!txnValidationResult) {
    return null;
  }

  // Check if it's a Solana transaction
  const isSolana =
    'result' in txnValidationResult && txnValidationResult.result?.simulation;

  if (isSolana) {
    // For Solana transactions
    const programs = getSolanaPrograms(txnValidationResult);
    const actions = getSolanaActions(txnValidationResult);

    return {
      type: 'solana',
      programs,
      actions,
    };
  } else {
    // For EVM transactions
    const contractAddress = getContractAddress(txnValidationResult);
    const contractName = getContractName(txnValidationResult, contractAddress);
    const functionName = getFunctionName(txnValidationResult);

    return {
      type: 'evm',
      contractAddress,
      contractName,
      functionName,
    };
  }
}
